import React, { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { Text } from 'shared/components/Text/Text';
import { Button } from 'shared/components/Button/Button';
import { useBreakpoints } from 'hooks/useWindowSize';
import {
  ActionsContainer,
  ContentItem,
  ContentList,
  GraphicContainer,
  OnboardingAutoPayContainer,
  OnboardingAutoPayContent,
} from './OnboardingAutoPay.styles';

const AutoPayGraphic = React.lazy(() => import('shared/components/icons/AutopayGraphic'));
const CheckmarkGraphic = React.lazy(() => import('shared/components/icons/CheckmarkGraphic'));

export function AutoPayEnrollment({
  achFailedVerification,
  enrolled,
  onClickEnroll,
  onClickNext,
  onClickSkipForNow,
}: {
  achFailedVerification: boolean;
  enrolled: boolean;
  onClickEnroll: () => void;
  onClickNext: () => void;
  onClickSkipForNow: () => void;
}) {
  const { t: tBase } = useTranslation('');
  const { t } = useTranslation('translation', {
    keyPrefix: enrolled ? 'onboarding.autoPay.enrolled' : 'onboarding.autoPay.notEnrolled',
  });
  const screenSize = useBreakpoints();

  const bodyList: string[] = t(achFailedVerification ? 'failedVerificationBody' : 'body', {
    returnObjects: true,
  }) as string[];

  return (
    <OnboardingAutoPayContainer>
      <GraphicContainer $enrolled={enrolled}>
        <Suspense fallback={<LoadingSpinner />}>
          {enrolled ? <CheckmarkGraphic /> : <AutoPayGraphic />}
        </Suspense>
      </GraphicContainer>

      <Text
        as="h1"
        $textAlign="center"
        $fontWeight={screenSize === 'lg' || screenSize === 'xl' ? 700 : 500}
      >
        {t('title')}
      </Text>
      <OnboardingAutoPayContent>
        <ContentList>
          {Array.isArray(bodyList) &&
            bodyList.map((item) => (
              <ContentItem key={item}>
                <Trans>{item}</Trans>
              </ContentItem>
            ))}
        </ContentList>
      </OnboardingAutoPayContent>

      <ActionsContainer>
        {enrolled ? (
          <Button styleVariant="primary" label={tBase('cta.next')} onClick={onClickNext} />
        ) : (
          <>
            <Button
              styleVariant="primary"
              label={t('enroll')}
              onClick={onClickEnroll}
              isFullWidth
            />
            <Button
              styleVariant="tertiary"
              label={t('skipForNow')}
              onClick={onClickSkipForNow}
              isFullWidth
            />
          </>
        )}
      </ActionsContainer>
    </OnboardingAutoPayContainer>
  );
}

export default AutoPayEnrollment;
